import { PlusOutlined, EditOutlined, LinkOutlined } from "@ant-design/icons";
import {
  Table,
  Button,
  Tag,
  Space,
  Input,
  InputNumber,
  Select,
  DatePicker,
  Typography,
  Empty,
  Grid
} from "antd";
import axios from "axios";
import dayjs from "dayjs";
import { useCallback, useEffect, useRef, useState } from "react";

import ProblemFormModal from "./ProblemFormModal";
import { clamp } from "../../../utils/responsiveHelpers";
import {
  abortLatestRequest,
  beginLatestRequest,
  CATALOGUE_INFO_TEXT,
  CATALOGUED_PLATFORM_OPTIONS,
  CONTENT_COMPLETENESS_DISPLAY_CONFIG,
  CONTENT_COMPLETENESS_FILTER_OPTIONS,
  DATE_DISPLAY_FORMAT,
  DETAIL_ORIGIN_DISPLAY_CONFIG,
  DETAIL_ORIGIN_FILTER_OPTIONS,
  EXTERNAL_PROBLEMS_API_BASE,
  EXTERNAL_PROBLEMS_PAGINATION_CONFIG,
  PLATFORM_ENUM,
  reportUnlessCancelled,
  SAMPLE_TEST_CASES_FILTER_OPTIONS,
  SHARED_INFO_TEXT,
  sumColumnWidths,
  COLUMN_WIDTH_ENUM
} from "../helper";
import InfoHint, { LabelWithHint } from "../InfoHint";

const { Text } = Typography;
const { Search } = Input;
const { RangePicker } = DatePicker;

/**
 * The one judge that grades on a word rather than a number and stores its
 * examples as test cases, so both the rating controls and the sample filter
 * would silently match nothing while it is selected.
 */
const PLATFORM_WITHOUT_RATINGS_OR_SAMPLES = PLATFORM_ENUM.LEETCODE_PROBLEM;

const CATALOGUE_FILTER_INITIAL_STATE = Object.freeze({
  platform: undefined,
  contentFidelity: undefined,
  isManuallyCurated: undefined,
  hasSamples: undefined,
  tags: undefined,
  difficultyLabels: undefined,
  ratings: undefined,
  minRating: undefined,
  maxRating: undefined,
  createdAfter: undefined,
  createdBefore: undefined,
  searchTerm: ""
});

/** Filter values whose options differ per platform, cleared when the platform changes. */
const PLATFORM_CHANGE_FILTER_RESET = Object.freeze({
  tags: undefined,
  ratings: undefined,
  difficultyLabels: undefined
});

/** Filters a platform without ratings or samples hides, cleared when it is picked. */
const HIDDEN_NUMERIC_FILTER_RESET = Object.freeze({
  hasSamples: undefined,
  minRating: undefined,
  maxRating: undefined
});

/** The multi-select for a judge that grades on a word, such as LeetCode. */
const DIFFICULTY_SCALE_FILTER = Object.freeze({
  filterKey: "difficultyLabels",
  placeholder: "Difficulty"
});

/** The multi-select for a judge that grades on a number. */
const RATING_SCALE_FILTER = Object.freeze({
  filterKey: "ratings",
  placeholder: "Rating"
});

const CATALOGUE_FILTER_OPTIONS_INITIAL_STATE = Object.freeze({
  tags: [],
  ratings: [],
  difficultyLabels: []
});

/** Column widths of the catalogue table; `scroll.x` is their sum. */
const CATALOGUE_TABLE_COLUMN_WIDTHS = Object.freeze({
  title: COLUMN_WIDTH_ENUM.LONG,
  platform: COLUMN_WIDTH_ENUM.SHORT,
  difficulty: COLUMN_WIDTH_ENUM.COUNT,
  tags: COLUMN_WIDTH_ENUM.TEXT,
  contentCompleteness: COLUMN_WIDTH_ENUM.WIDER,
  detailOrigin: COLUMN_WIDTH_ENUM.WIDE,
  updatedAt: COLUMN_WIDTH_ENUM.SHORT,
  actions: COLUMN_WIDTH_ENUM.ICON
});

/**
 * One filter's values as a Select's options.
 *
 * @param {Array<string|number>} optionValues
 * @returns {Array<{label: string, value: string|number}>}
 */
const toFilterOptions = (optionValues) =>
  optionValues.map((optionValue) => ({ label: String(optionValue), value: optionValue }));

/**
 * Joins a multi-select filter's values into the comma-separated form the list
 * endpoint takes.
 *
 * @param {Array<string|number>|undefined} selectedValues
 * @returns {string|undefined} Undefined when nothing is selected, so the param is omitted.
 */
const joinFilterValues = (selectedValues) =>
  selectedValues?.length ? selectedValues.join(",") : undefined;

/**
 * Builds the query params of `GET /list` from the filters and pager.
 *
 * @param {object} params
 * @param {typeof CATALOGUE_FILTER_INITIAL_STATE} params.filters
 * @param {number} params.currentPage
 * @param {number} params.pageSize
 * @returns {Record<string, any>}
 */
const buildListQueryParams = ({ filters, currentPage, pageSize }) => {
  const { tags, difficultyLabels, ratings, searchTerm, ...passThroughFilters } = filters;

  return {
    ...passThroughFilters,
    page: currentPage,
    limit: pageSize,
    tags: joinFilterValues(tags),
    difficultyLabels: joinFilterValues(difficultyLabels),
    ratings: joinFilterValues(ratings),
    searchTerm: searchTerm || undefined
  };
};

/**
 * Paginated, filterable browser over every problem the catalogue holds,
 * whichever way its details got in. Doubles as the entry point for creating a
 * problem from a pasted link and for editing one already found through the
 * reconciliation queue.
 *
 * The tag and difficulty pickers are populated from the catalogue itself and
 * reload whenever the platform changes, so an admin is never offered a value
 * that would return an empty page.
 *
 * @param {object} props
 * @param {string} props.ROOT_URL
 * @param {boolean} props.hasManagementAccess - False leaves the screen read-only, matching the
 *   admin's `externalProblems.hasManagementAccess` permission.
 */
const Catalogue = ({ ROOT_URL, hasManagementAccess }) => {
  const [problems, setProblems] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasLoadedList, setHasLoadedList] = useState(false);
  const [filters, setFilters] = useState(CATALOGUE_FILTER_INITIAL_STATE);
  const [currentPage, setCurrentPage] = useState(EXTERNAL_PROBLEMS_PAGINATION_CONFIG.DEFAULT_PAGE);
  const [pageSize, setPageSize] = useState(EXTERNAL_PROBLEMS_PAGINATION_CONFIG.DEFAULT_PAGE_SIZE);
  const [totalItems, setTotalItems] = useState(0);

  const [catalogueFilterOptions, setCatalogueFilterOptions] = useState(CATALOGUE_FILTER_OPTIONS_INITIAL_STATE);
  const [isLoadingFilterOptions, setIsLoadingFilterOptions] = useState(false);

  const [isFormModalOpen, setIsFormModalOpen] = useState(false);
  const [editingUniquePublicId, setEditingUniquePublicId] = useState(null);

  const listAbortControllerRef = useRef(null);
  const filterOptionsAbortControllerRef = useRef(null);

  const screens = Grid.useBreakpoint();
  const isMobileView = screens.md === false;

  // A platform that grades on a word has no rating range to narrow, and keeps
  // its examples somewhere the samples filter cannot see.
  const { platform: selectedPlatform, hasSamples: selectedHasSamples, tags: selectedTags } = filters;
  const isEmptyStateVisible = hasLoadedList && !isLoading;
  const areNumericFiltersHidden = selectedPlatform === PLATFORM_WITHOUT_RATINGS_OR_SAMPLES;
  const { tags: tagOptionValues } = catalogueFilterOptions;
  const { filterKey: scaleFilterKey, placeholder: scaleFilterPlaceholder } =
    areNumericFiltersHidden ? DIFFICULTY_SCALE_FILTER : RATING_SCALE_FILTER;

  const columns = [
    {
      title: "Title",
      dataIndex: "title",
      key: "title",
      width: CATALOGUE_TABLE_COLUMN_WIDTHS.title,
      render: (title, { externalUrl, externalId }) => (
        <Space direction="vertical" size={0}>
          <Text strong>{title}</Text>
          {externalUrl && (
            <a href={externalUrl} target="_blank" rel="noopener noreferrer">
              <LinkOutlined /> {externalId}
            </a>
          )}
        </Space>
      )
    },
    {
      title: <LabelWithHint label="Platform" infoText={SHARED_INFO_TEXT.PLATFORM} />,
      dataIndex: "platformDisplayName",
      key: "platformDisplayName",
      width: CATALOGUE_TABLE_COLUMN_WIDTHS.platform,
      responsive: ["sm"],
      render: (platformDisplayName) => <Tag color="blue">{platformDisplayName}</Tag>
    },
    {
      title: "Rating / Difficulty",
      key: "difficulty",
      width: CATALOGUE_TABLE_COLUMN_WIDTHS.difficulty,
      responsive: ["md"],
      render: (_, { rating, difficultyLabel }) => rating ?? difficultyLabel ?? "--"
    },
    {
      title: "Tags",
      dataIndex: "tags",
      key: "tags",
      width: CATALOGUE_TABLE_COLUMN_WIDTHS.tags,
      responsive: ["lg"],
      render: (problemTags) => (
        <Space size={[4, 4]} wrap>
          {problemTags.map((tag) => (
            <Tag key={tag}>{tag}</Tag>
          ))}
        </Space>
      )
    },
    {
      title: (
        <LabelWithHint
          label="Content completeness"
          infoText={CATALOGUE_INFO_TEXT.CONTENT_COMPLETENESS}
        />
      ),
      dataIndex: "contentFidelity",
      key: "contentFidelity",
      width: CATALOGUE_TABLE_COLUMN_WIDTHS.contentCompleteness,
      responsive: ["lg"],
      render: (contentFidelity) => {
        const { label, color } = CONTENT_COMPLETENESS_DISPLAY_CONFIG[contentFidelity] ?? {};

        return <Tag color={color}>{label}</Tag>;
      }
    },
    {
      title: <LabelWithHint label="Details from" infoText={CATALOGUE_INFO_TEXT.DETAIL_ORIGIN} />,
      dataIndex: "isManuallyCurated",
      key: "isManuallyCurated",
      width: CATALOGUE_TABLE_COLUMN_WIDTHS.detailOrigin,
      align: "center",
      responsive: ["md"],
      render: (isManuallyCurated) => {
        const { label, color } = isManuallyCurated
          ? DETAIL_ORIGIN_DISPLAY_CONFIG.ADMIN_ENTERED
          : DETAIL_ORIGIN_DISPLAY_CONFIG.IMPORTED;

        return <Tag color={color}>{label}</Tag>;
      }
    },
    {
      title: "Updated",
      dataIndex: "updatedAt",
      key: "updatedAt",
      width: CATALOGUE_TABLE_COLUMN_WIDTHS.updatedAt,
      responsive: ["md"],
      render: (updatedAt) => dayjs(updatedAt).format(DATE_DISPLAY_FORMAT)
    },
    {
      title: "Actions",
      key: "actions",
      width: CATALOGUE_TABLE_COLUMN_WIDTHS.actions,
      align: "center",
      fixed: isMobileView ? undefined : "right",
      render: (_, { uniquePublicId }) => (
        <Button
          type="text"
          shape="circle"
          icon={<EditOutlined />}
          disabled={!hasManagementAccess}
          onClick={() => openEditModal(uniquePublicId)}
        />
      )
    }
  ];

  /**
   * Loads the page of catalogue problems matching the current filters and pager.
   * Only the latest call writes state, and a failure leaves the rows on screen.
   */
  const fetchProblems = useCallback(() => {
    const { signal } = beginLatestRequest(listAbortControllerRef);

    setIsLoading(true);
    axios
      .get(`${ROOT_URL}/${EXTERNAL_PROBLEMS_API_BASE}/list`, {
        signal,
        params: buildListQueryParams({ filters, currentPage, pageSize })
      })
      .then((response) => {
        const { problems: fetchedProblems, pagination } = response.data;
        setProblems(fetchedProblems);
        setTotalItems(pagination.totalItems);
        setHasLoadedList(true);
      })
      .catch(reportUnlessCancelled)
      .finally(() => {
        if (!signal.aborted) {
          setIsLoading(false);
        }
      });
  }, [ROOT_URL, currentPage, pageSize, filters]);

  /**
   * Merges a partial filter change into the current filters and returns the
   * pager to its first page. Changing the platform also drops the tag,
   * difficulty and rating picks, whose options belong to the previous platform,
   * and the numeric filters a LeetCode view hides.
   *
   * @param {Partial<typeof CATALOGUE_FILTER_INITIAL_STATE>} filterPatch
   */
  const updateFilter = (filterPatch) => {
    const platformResetPatch = "platform" in filterPatch ? PLATFORM_CHANGE_FILTER_RESET : {};
    const hiddenNumericFilterResetPatch =
      filterPatch.platform === PLATFORM_WITHOUT_RATINGS_OR_SAMPLES ? HIDDEN_NUMERIC_FILTER_RESET : {};

    setFilters((previousFilters) => ({
      ...previousFilters,
      ...platformResetPatch,
      ...hiddenNumericFilterResetPatch,
      ...filterPatch
    }));
    setCurrentPage(EXTERNAL_PROBLEMS_PAGINATION_CONFIG.DEFAULT_PAGE);
  };

  /**
   * Applies the created-date range as whole-day bounds, or clears both bounds.
   *
   * @param {Array<import("dayjs").Dayjs>|null} dates - Start and end of the range, null when cleared.
   */
  const handleCreatedRangeChange = (dates) => {
    if (!dates) {
      updateFilter({ createdAfter: undefined, createdBefore: undefined });
      return;
    }

    const [rangeStart, rangeEnd] = dates;
    updateFilter({
      createdAfter: rangeStart.startOf("day").toISOString(),
      createdBefore: rangeEnd.endOf("day").toISOString()
    });
  };

  /** Opens the problem form empty, to create a problem. */
  const openCreateModal = () => {
    setEditingUniquePublicId(null);
    setIsFormModalOpen(true);
  };

  /**
   * Opens the problem form on an existing problem.
   *
   * @param {string} uniquePublicId - Public id of the problem to edit.
   */
  const openEditModal = (uniquePublicId) => {
    setEditingUniquePublicId(uniquePublicId);
    setIsFormModalOpen(true);
  };

  /** Closes the problem form without saving. */
  const closeFormModal = () => setIsFormModalOpen(false);

  /**
   * Applies a page or page-size change from the pager.
   *
   * @param {number} page
   * @param {number} size
   */
  const handlePageChange = (page, size) => {
    setCurrentPage(page);
    setPageSize(size);
  };

  /** Closes the problem form and reloads the list after a save. */
  const handleSaved = () => {
    setIsFormModalOpen(false);
    fetchProblems();
  };

  useEffect(() => {
    fetchProblems();
  }, [fetchProblems]);

  // Reloaded per platform, because difficulty is not one scale across judges
  // and a combined list would offer values that cannot coexist with the
  // platform selected.
  useEffect(() => {
    const { signal } = beginLatestRequest(filterOptionsAbortControllerRef);

    setIsLoadingFilterOptions(true);
    axios
      .get(`${ROOT_URL}/${EXTERNAL_PROBLEMS_API_BASE}/filter-options`, {
        signal,
        params: { platform: selectedPlatform }
      })
      .then((response) => setCatalogueFilterOptions(response.data))
      .catch(reportUnlessCancelled)
      .finally(() => {
        if (!signal.aborted) {
          setIsLoadingFilterOptions(false);
        }
      });
  }, [ROOT_URL, selectedPlatform]);

  useEffect(
    () => () => {
      abortLatestRequest(listAbortControllerRef);
      abortLatestRequest(filterOptionsAbortControllerRef);
    },
    []
  );

  return (
    <div style={{ padding: clamp(12, 24) }}>
      <div
        style={{
          marginBottom: 16,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 12
        }}
      >
        <Space wrap style={{ flex: 1, minWidth: 0 }}>
          <Search
            placeholder="Search by title or external ID"
            allowClear
            style={{ minWidth: 200, flex: 1 }}
            onSearch={(enteredSearchTerm) => updateFilter({ searchTerm: enteredSearchTerm })}
          />
          <Select
            placeholder="Platform"
            allowClear
            style={{ minWidth: 140, flex: 1 }}
            options={CATALOGUED_PLATFORM_OPTIONS}
            onChange={(platform) => updateFilter({ platform })}
          />
          <Select
            placeholder="Content completeness"
            allowClear
            style={{ minWidth: 170, flex: 1 }}
            options={CONTENT_COMPLETENESS_FILTER_OPTIONS}
            onChange={(contentFidelity) => updateFilter({ contentFidelity })}
          />
          <Select
            placeholder="Details from"
            allowClear
            style={{ minWidth: 160, flex: 1 }}
            options={DETAIL_ORIGIN_FILTER_OPTIONS}
            onChange={(isManuallyCurated) => updateFilter({ isManuallyCurated })}
          />
          {!areNumericFiltersHidden && (
            <Space size={4}>
              <Select
                placeholder="Sample test cases"
                allowClear
                style={{ minWidth: 180 }}
                options={SAMPLE_TEST_CASES_FILTER_OPTIONS}
                value={selectedHasSamples}
                onChange={(hasSamples) => updateFilter({ hasSamples })}
              />
              <InfoHint title={CATALOGUE_INFO_TEXT.SAMPLE_TEST_CASES} />
            </Space>
          )}
          <Space size={4}>
            <Select
              mode="multiple"
              placeholder="Tags"
              allowClear
              maxTagCount="responsive"
              style={{ minWidth: 200 }}
              loading={isLoadingFilterOptions}
              options={toFilterOptions(tagOptionValues)}
              value={selectedTags}
              onChange={(chosenTags) => updateFilter({ tags: chosenTags })}
            />
            <InfoHint title={CATALOGUE_INFO_TEXT.TAG_FILTER} />
          </Space>
          <Space size={4}>
            <Select
              mode="multiple"
              placeholder={scaleFilterPlaceholder}
              allowClear
              maxTagCount="responsive"
              style={{ minWidth: 200 }}
              loading={isLoadingFilterOptions}
              options={toFilterOptions(catalogueFilterOptions[scaleFilterKey])}
              value={filters[scaleFilterKey]}
              onChange={(selectedValues) =>
                updateFilter({ [scaleFilterKey]: selectedValues })
              }
            />
            <InfoHint title={CATALOGUE_INFO_TEXT.DIFFICULTY_FILTER} />
          </Space>
          {!areNumericFiltersHidden && (
            <Space.Compact>
              <InputNumber
                placeholder="Min rating"
                style={{ width: 110 }}
                onChange={(minRating) => updateFilter({ minRating: minRating ?? undefined })}
              />
              <InputNumber
                placeholder="Max rating"
                style={{ width: 110 }}
                onChange={(maxRating) => updateFilter({ maxRating: maxRating ?? undefined })}
              />
            </Space.Compact>
          )}
          <RangePicker
            placeholder={["Created after", "Created before"]}
            style={{ minWidth: 220, flex: 1 }}
            onChange={handleCreatedRangeChange}
          />
        </Space>

        <Button
          type="primary"
          icon={<PlusOutlined />}
          disabled={!hasManagementAccess}
          onClick={openCreateModal}
        >
          New Problem
        </Button>
      </div>

      <Text type="secondary" style={{ display: "block", marginBottom: 12 }}>
        {hasLoadedList
          ? `${totalItems} problem${totalItems === 1 ? "" : "s"} matching current filters`
          : "Loading problems..."}
      </Text>

      <Table
        rowKey="uniquePublicId"
        columns={columns}
        dataSource={problems}
        loading={isLoading}
        locale={{
          emptyText: isEmptyStateVisible ? (
            <Empty description="No problem matches these filters." />
          ) : (
            <div style={{ minHeight: 120 }} />
          )
        }}
        bordered
        size={isMobileView ? "small" : "middle"}
        pagination={{
          current: currentPage,
          pageSize,
          total: totalItems,
          showSizeChanger: !isMobileView,
          simple: isMobileView,
          pageSizeOptions: EXTERNAL_PROBLEMS_PAGINATION_CONFIG.PAGE_SIZE_OPTIONS,
          onChange: handlePageChange
        }}
        scroll={{ x: isMobileView ? "max-content" : sumColumnWidths(CATALOGUE_TABLE_COLUMN_WIDTHS) }}
      />

      {isFormModalOpen && (
        <ProblemFormModal
          open={isFormModalOpen}
          uniquePublicId={editingUniquePublicId}
          onClose={closeFormModal}
          onSaved={handleSaved}
          ROOT_URL={ROOT_URL}
        />
      )}
    </div>
  );
};

export default Catalogue;
